import { useState, useRef, useEffect } from 'react'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import Button from '../components/UI/Button'
import Input from '../components/UI/Input'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import CreateProductModal from '../components/Modals/CreateProductModal'
import ConfirmationModal from '../components/Modals/ConfirmationModal'
import productsAPI from '../services/productsAPI'
import toast from 'react-hot-toast'
import { Search, Plus, Filter, MoreVertical, Edit, Trash2, Eye, Package, Settings, ExternalLink } from 'lucide-react'

export default function Products() {
  const { user, hasPermission, isControlCenter } = useAuth()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [viewMode, setViewMode] = useState('grid') // grid, list, card
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showActionsFor, setShowActionsFor] = useState(null) // product id for 3-dot menu
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [productToDelete, setProductToDelete] = useState(null)
  const [deleteError, setDeleteError] = useState(null)

  const { data: productsData, isLoading, refetch } = useQuery(
    ['products', page, status, searchTerm],
    () => productsAPI.getAll({ 
      page, 
      limit: 12, 
      status, 
      search: searchTerm 
    }),
    { 
      enabled: true, // Allow all authenticated users to view products
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 15 * 60 * 1000, // 15 minutes
      refetchOnMount: true, // Refetch when returning to listing so status updates are visible
    }
  )

  const products = productsData?.data?.data?.products || []
  const pagination = productsData?.data?.data?.pagination || {}

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
    setPage(1)
  }

  const handleStatusChange = (e) => {
    setStatus(e.target.value)
    setPage(1)
  }

  const handleViewProduct = (productId) => {
    navigate(`/products/${productId}`)
  }

  const handleEditProduct = (productId) => {
    navigate(`/products/${productId}/edit`)
  }

  const promptDeleteProduct = async (product) => {
    setProductToDelete(product)
    setDeleteError(null)
    
    // Check if product can be deleted (has active subscriptions)
    try {
      const checkResponse = await productsAPI.checkDeletion(product.id)
      
       if (!checkResponse.data?.canDelete) {
         // Product has active subscriptions - show error message
         setDeleteError('Cannot delete product with active subscriptions.')
         setShowDeleteModal(true)
      } else {
        // Product can be deleted - show normal confirmation modal
        setShowDeleteModal(true)
      }
    } catch (error) {
      console.error('Error checking product deletion:', error)
      // If check fails, still show confirmation modal (let delete attempt handle the error)
      setShowDeleteModal(true)
    }
  }

  const confirmDeleteProduct = async () => {
    if (!productToDelete) return
    
    // If there's already an error (active subscriptions), don't try to delete again
    if (deleteError) {
      setShowDeleteModal(false)
      setProductToDelete(null)
      setDeleteError(null)
      setShowActionsFor(null)
      return
    }
    
    try {
      await productsAPI.delete(productToDelete.id)
      toast.success('Product deleted successfully!')
      setShowDeleteModal(false)
      setProductToDelete(null)
      setDeleteError(null)
      setShowActionsFor(null)
      await refetch()
    } catch (error) {
      console.error('Error deleting product:', error)
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to delete product'
      const errorDetails = error.response?.data?.details
      
       // Show error message if there are active subscriptions
       if (error.response?.status === 400 && errorDetails) {
         setDeleteError('Cannot delete product with active subscriptions.')
       } else {
         setDeleteError(errorMessage)
       }
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      draft: { color: 'bg-yellow-100 text-yellow-800', label: 'Draft' }
    }
    
    const config = statusConfig[status] || statusConfig.inactive
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionsFor !== null) {
        // Check if click is outside any dropdown button or menu
        const isClickInsideDropdown = event.target.closest('.dropdown-container')
        if (!isClickInsideDropdown) {
          setShowActionsFor(null)
        }
      }
    }

    if (showActionsFor !== null) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showActionsFor])

  // Allow all authenticated users to view products (SAS user check is handled by backend middleware)
  // The backend will return 403 if user is not a SAS user

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Products</h1>
          <p className="text-secondary-600 mt-1">Manage your product catalog and pricing</p>
        </div>
        {hasPermission('products.create') && (
          <Button 
            className="mt-4 sm:mt-0"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Product
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <Input
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={status}
                onChange={handleStatusChange}
                className="input w-full"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            {/* <Button variant="outline">
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button> */}
          </div>
        </CardContent>
      </Card>

      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-secondary-600">View:</span>
          <div className="flex bg-secondary-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-white text-secondary-900 shadow-sm' 
                  : 'text-secondary-600 hover:text-secondary-900'
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'list' 
                  ? 'bg-white text-secondary-900 shadow-sm' 
                  : 'text-secondary-600 hover:text-secondary-900'
              }`}
            >
              List
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'card' 
                  ? 'bg-white text-secondary-900 shadow-sm' 
                  : 'text-secondary-600 hover:text-secondary-900'
              }`}
            >
              Card
            </button>
          </div>
        </div>
        <div className="text-sm text-secondary-600">
          {pagination.total || 0} products
        </div>
      </div>

      {/* Products Display */}
      <Card>
        <CardContent className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <>
              {viewMode === 'grid' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map((product) => (
                    <Card key={product.id} className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        {/* Product Image */}
                        {product.image_url && (
                          <div className="mb-4 rounded-lg overflow-hidden bg-secondary-100">
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-full h-48 object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none'
                              }}
                            />
                          </div>
                        )}
                        
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-lg font-semibold text-secondary-900">
                                {product.name}
                              </h3>
                              {product.category && (
                                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                  {product.category}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-secondary-600 mb-3 line-clamp-2">
                              {product.description || 'No description available'}
                            </p>
                            {product.url && (
                              <a 
                                href={product.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 mb-2"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Visit Website
                              </a>
                            )}
                          </div>
                          {hasPermission('products.delete') && (
                            <div className="relative dropdown-container">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setShowActionsFor(showActionsFor === product.id ? null : product.id)}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                              {showActionsFor === product.id && (
                                <div className="absolute right-0 mt-2 w-40 bg-white border border-secondary-200 rounded-md shadow-lg z-10">
                                  <button 
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-secondary-50 text-red-600 flex items-center"
                                    onClick={() => { setShowActionsFor(null); promptDeleteProduct(product) }}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" /> Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-secondary-600">Status:</span>
                            {getStatusBadge(product.status)}
                          </div>
                          
                          <div className="flex items-center justify-between text-sm text-secondary-600">
                            <span>Modules: {product.module_count || 0}</span>
                            <span>Features: {product.feature_count || 0}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-secondary-200">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => handleViewProduct(product.id)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          {hasPermission('products.update') && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-1"
                              onClick={() => handleEditProduct(product.id)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {viewMode === 'list' && (
                <div className="overflow-x-auto">
                  <table className="table w-full">
                    <thead className="table-header">
                      <tr className="table-row">
                        <th className="table-head">Product</th>
                        <th className="table-head">Status</th>
                        <th className="table-head">Modules</th>
                        <th className="table-head">Features</th>
                        <th className="table-head">Created</th>
                        <th className="table-head">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {products.map((product) => (
                        <tr key={product.id} className="table-row">
                          <td className="table-cell">
                            <div className="flex items-center">
                              {product.image_url ? (
                                <div className="h-10 w-10 rounded-lg overflow-hidden bg-secondary-100 flex-shrink-0">
                                  <img 
                                    src={product.image_url} 
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none'
                                      e.target.parentElement.innerHTML = '<div class="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center"><svg class="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg></div>'
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                                  <Package className="h-5 w-5 text-primary-600" />
                                </div>
                              )}
                              <div className="ml-3">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-secondary-900">
                                    {product.name}
                                  </p>
                                  {product.category && (
                                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">
                                      {product.category}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-secondary-500 line-clamp-1">
                                  {product.description || 'No description'}
                                </p>
                                {product.url && (
                                  <a 
                                    href={product.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 mt-1"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    {product.url}
                                  </a>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="table-cell">
                            {getStatusBadge(product.status)}
                          </td>
                          <td className="table-cell">
                            <p className="text-sm text-secondary-900">
                              {product.module_count || 0}
                            </p>
                          </td>
                          <td className="table-cell">
                            <p className="text-sm text-secondary-900">
                              {product.feature_count || 0}
                            </p>
                          </td>
                          <td className="table-cell">
                            <p className="text-sm text-secondary-900">
                              {new Date(product.created_at).toLocaleDateString()}
                            </p>
                          </td>
                          <td className="table-cell">
                            <div className="flex items-center space-x-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleViewProduct(product.id)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {hasPermission('products.update') && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleEditProduct(product.id)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              )}
                              {hasPermission('products.delete') && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => promptDeleteProduct(product)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {viewMode === 'card' && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  {products.map((product) => (
                    <Card key={product.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        {/* Product Image */}
                        {product.image_url && (
                          <div className="mb-3 rounded-lg overflow-hidden bg-secondary-100">
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-full h-32 object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none'
                              }}
                            />
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between mb-3">
                          {product.image_url ? (
                            <div className="h-8 w-8 rounded-lg bg-primary-100 flex items-center justify-center">
                              <Package className="h-4 w-4 text-primary-600" />
                            </div>
                          ) : (
                            <div className="h-8 w-8 rounded-lg bg-primary-100 flex items-center justify-center">
                              <Package className="h-4 w-4 text-primary-600" />
                            </div>
                          )}
                          {getStatusBadge(product.status)}
                        </div>
                        
                        <div className="mb-2">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-secondary-900 line-clamp-1">
                              {product.name}
                            </h3>
                          </div>
                          {product.category && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full inline-block mb-1">
                              {product.category}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-secondary-600 mb-3 line-clamp-2">
                          {product.description || 'No description available'}
                        </p>
                        
                        {product.url && (
                          <a 
                            href={product.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 mb-3"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Visit Website
                          </a>
                        )}
                        
                        <div className="flex items-center space-x-1">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => handleViewProduct(product.id)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                          {hasPermission('products.update') && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-1"
                              onClick={() => handleEditProduct(product.id)}
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {products.length === 0 && (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-secondary-900 mb-2">No products found</h3>
                  <p className="text-secondary-600 mb-4">
                    {searchTerm || status 
                      ? 'Try adjusting your search or filters'
                      : 'Get started by creating your first product'
                    }
                  </p>
                  {hasPermission('products.create') && (
                    <Button onClick={() => setShowCreateModal(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Product
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-secondary-700">
                Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} results
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === pagination.pages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Product Modal */}
      <CreateProductModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { 
          setShowDeleteModal(false)
          setProductToDelete(null)
          setDeleteError(null)
        }}
        onConfirm={confirmDeleteProduct}
        title={deleteError ? "Cannot Delete Product" : `Delete "${productToDelete?.name || 'Product'}"?`}
        message={deleteError || `Are you sure you want to delete "${productToDelete?.name || ''}"? This will permanently delete the product and all its associated data. This action cannot be undone.`}
        confirmText={deleteError ? "Got it" : "Delete"}
        cancelText={deleteError ? null : "Cancel"}
        type={deleteError ? "warning" : "danger"}
      />
    </div>
  )
}
